import React, { useState, Fragment } from "react";
import {
	useTable,
	useFlexLayout,
	useExpanded,
	useResizeColumns,
} from "react-table";
import { formatValue } from "./Utils.js";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../tailwind.config.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { UtilizationCharts } from "./UtilizationCharts.jsx";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { useNavigate } from "react-router-dom";
// import { Tooltip } from "antd";
import "./ResourceTable.css";
import Tooltip from "./form-components/Tooltip.jsx";

const colors = resolveConfig(tailwindConfig).theme.colors;

function Table({ columns, data, setHoveredRow, expandAll }) {
	const openResourceTypes = sessionStorage.getItem('openResourceTypes');
	const openRows = openResourceTypes ? openResourceTypes.split(",") : [];
	let defaultExpandedRows = {};

	data.forEach((element, index) => {
		if (openRows.includes(element.resource_type) || expandAll) {
			defaultExpandedRows[index] = true;
		}
	});

	const defaultColumn = React.useMemo(
		() => ({
		  minWidth: 30,
		  width: 150,
		  maxWidth: 800,
		}),
		[]
	  )

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable(
			{
				columns,
				data,
				defaultColumn,
				autoResetExpanded: false,
				collapseOnDataChange: false,
				initialState: { expanded: defaultExpandedRows },
			},
			useFlexLayout,
			useExpanded,
			useResizeColumns
		);


	return (
		<div
			{...getTableProps()}
			className="flex w-full cursor-default flex-auto flex-col"
		>
			<div className="py-2 border-b border-border pl-4 text-text-2 sticky top-0 z-50 bg-card">
				{headerGroups.map((headerGroup) => (
					<div {...headerGroup.getHeaderGroupProps()} className="tr">
						{headerGroup.headers.map((column) => (
							<div
								{...column.getHeaderProps()}
								className="th uppercase text-sm"
							>
								{column.render("Header")}
								<div
									{...column.getResizerProps()}
									className={`resizer ${column.isResizing ? "isResizing" : ""}`}
								/>
							</div>
						))}
					</div>
				))}
			</div>

			<div {...getTableBodyProps()} className="">	
				{rows.map((row, index) => {
					prepareRow(row);

					return (
						<div
							{...row.getRowProps({
							})}
							className={"tr border-b border-border hover:bg-surface-2 h-[77px]"}
						>
							{row.cells.map((cell) => {
								return (
									<div
										{...cell.getCellProps()}
										className="td truncate pl-1 pt-7 pb-3 text-sm"
									>
										{cell.render("Cell")}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
}

const UntaggedResourceTable = ({ data, grouping, toggleRow, openRows, expandAll }) => {
	const [, setHoveredRow] = useState(-1);
	const navigate = useNavigate();

	const openDetails = (resourceID, type) => {
		if (type === "Resource") {
			navigate("/resource/" + resourceID);
		}
	}
	
	const columnDefs = [
		{
			accessor: "plus",
			id: "expander",
			Header: (row) => <div className=""><span data-tooltip-id="instantTooltip" data-tooltip-content="The name of the resource">Resource</span></div>,
			Cell: ({ row }) => {
				const toggleRowExpandedProps = row.getToggleRowExpandedProps();
				const onClick = async (event) => {
					toggleRowExpandedProps.onClick(event);

					if (toggleRow) toggleRow(row.original.resource_type);
					// if (!row.isExpanded && !row.original.subRows) {
						// retrieveAssets(row.original.resource_type);
					// }
				};

				const margin = {
                    paddingLeft: 36 + (row.depth*36)
                }
				
				if (!row.original.resource_name) {
					if (row.depth === 1 && row.original.subRows.length > 0) {
						// if the depth = 1 and there are subrows, show the expander but put padding on the left
						return (
							<>
							<span {...row.getToggleRowExpandedProps()} onClick={onClick} className="text-left text-text font-semibold pl-12 cursor-pointer">
								{row.isExpanded ? (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-down",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								) : (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-right",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								)}
								{row.original.resource_type}
							</span>
							</>
						)							
					} else {
						// if the row is a resource group, show the expander
						return (
							<>
							<span {...row.getToggleRowExpandedProps()} onClick={onClick} className="text-left text-text font-semibold cursor-pointer">
								{row.isExpanded ? (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-down",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								) : (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-right",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								)}
								{row.original.resource_cluster}
							</span>
							</>
						)							
					}
						

				} else {
					return (
						<div 
							style={margin}
							className="text-left text-text cursor-pointer"
							onClick={(e) => { openDetails(row.original.resource_id, row.original.type)} }
						>
							<Tooltip
								title={row.original.resource_name}
								color={colors["text-2"]}
								placement="right"
							>
								{row.original.resource_name}
								</Tooltip>
						</div>
					);
				}
			},
			// minWidth: 30,
			width: 400,
			// maxWidth: 30,
		},
		{
			accessor: "resource_type",
			Header: (row) => <div className=""><span data-tooltip-id="instantTooltip" data-tooltip-content="The type of the resource">Type</span></div>,
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-left text-text">
							{cell.row.original.resource_type}
						</div>
					);
				}
			},
			width: 200,
		},
		{
			accessor: "vendor_name",
			// Header: (row) => <div className="">Vendor</div>,
			Cell: ({ row }) => {
				if (!row.original.resource_name || row.original.type === "Product") {
					return (
						<div />
					);
				} else {
					return (
						<div className="text-right text-text font-semibold">
					 		<FontAwesomeIcon icon={faGoogle} className="text-text-2" />
						</div>
					);
				}
			},
			minWidth: 30,
			width: 30,
			maxWidth: 30,
		},
		{
			accessor: "units",
			Header: (row) => <div className="text-right mr-2"><span data-tooltip-id="instantTooltip" data-tooltip-content="The number of units of the resource that is currently being used">Untagged Units</span></div>,
			Cell: (row) => (
				<div className="text-right text-text font-semibold">
					{formatValue(row.value, "decimal", false)}
				</div>
			),
			width: 150,
		},
		{
			accessor: "cost",
			Header: (row) => <div className="text-right mr-2"><span data-tooltip-id="instantTooltip" data-tooltip-content="The cost of the resource that has not been tagged">Untagged cost</span></div>,
			Cell: (row) => {
				if (!row.row.original.vendor_name === "Google Cloud Platform") {
				return (
					<div className="text-right text-text font-semibold">
						{formatValue(row.value, "currency", false)}
					</div>
				)}
			else { return (<div></div>)}}
			,
			width: 140
		},
		// {
		// 	accessor: "usage_amount",
		// 	Header: (row) => <div className="text-right mr-2">Usage</div>,
		// 	Cell: (row) => (
		// 		<div className="text-right text-coolgrey1 font-semibold">
		// 			{formatValue(row.value, "decimal", false)}
		// 		</div>
		// 	),
		// 	width: 100,
		// 	maxWidth: 100,
		// },
		{
			accessor: "utilization",
			Header: (row) => <div className="text-left ml-4"><span data-tooltip-id="instantTooltip" data-tooltip-content="The utilization of the resource during the selected time period">Utilization</span></div>,
			Cell: (row) => {
				if (!row.row.original.resource_name || row.row.original.type === "Product") {
					return (
						<div />
					);
				} else {
					return (
						<UtilizationCharts data={row.row.original} />
					);
				}
			},
			// Cell: (row) => <UtilizationCharts data={row.row.original} />,
			width: 300,
			maxWidth: 300,
		},
		// {
		// 	accessor: "actions",
		// 	Header: (row) => <div className="text-left mr-4 ml-4">Actions</div>,
		// 	Cell: (row) => {
		// 		const navigate = useNavigate();
		// 		const openDetails = (productID) => {
        //             navigate("/product-overview/" + productID)
        //         }

		// 		if (row.row.original.resource_name) {
		// 			return (
		// 				<div className={"text-left text-coolgrey1 font-semibold mr-4 " + (row.row.id == hoveredRow ? "flex" : "hidden")}>
		// 				<div className="border rounded-lg cursor-pointer px-2 w-[80px] leading-6 bg-lightblue3 text-coolgrey10" onClick={(e) => openDetails(row.row.original.product_id)}>
		// 					<FontAwesomeIcon
		// 						icon={icon({
		// 							name: "arrow-right",
		// 							family: "sharp",
		// 							style: "solid",
		// 						})}
		// 						className="text-coolgrey10 mr-1"
		// 					/>
		// 					Details
		// 				</div>
		// 			</div>
						
		// 			)
		// 		} else {
		// 			return <div></div>
		// 		}
		// 	},
		// 	width: 100,
		// 	maxWidth: 100,
		// },
	];

	if (data && data.length > 0) {
		return (
			<Fragment>
				<Tooltip />
				<Table
					className="flex-auto"
					columns={columnDefs}
					data={data}
					renderRowSubComponent
					expandedRows={false}
					openRows={openRows}
					setHoveredRow={setHoveredRow}
					expandAll={expandAll}
				/>
			</Fragment>
		);
	} else {
		return (
			<Fragment>
				<div className="ml-10 font-semibold text-3xl text-text"></div>
			</Fragment>
		);
	}
};

export default UntaggedResourceTable;
